const MS_OFFICE_CLASS_PREFIX = 'Mso';
const MS_OFFICE_STYLE_DECLARATION = /(^|;)\s*mso-[^;]*/gi;
const DISCARDED_ELEMENTS = [
  'style',
  'script',
  'template',
  'link',
  'meta',
  'title',
  'base',
  'iframe',
  'frame',
  'frameset',
  'object',
  'embed',
  'applet',
  'form',
  'input',
  'button',
  'textarea',
  'select',
].join(', ');
const EVENT_HANDLER_ATTRIBUTE_PREFIX = 'on';
const URL_ATTRIBUTES = new Set([
  'href',
  'src',
  'action',
  'formaction',
  'xlink:href',
]);
const SAFE_URL_SCHEMES = new Set(['http', 'https', 'mailto', 'tel', 'cid']);
const INLINE_IMAGE_DATA_URL = /^data:image\//i;
const URL_SCHEME = /^([a-z][a-z0-9+.-]*):/i;
const ASCII_TAB_OR_NEWLINE = /[\t\n\r]/g;
const SPACE_CODE = 0x20;

// WHATWG URL spec "C0 control or space": every code point up to and including U+0020.
function isC0ControlOrSpace(codePoint: number | undefined): boolean {
  return codePoint !== undefined && codePoint <= SPACE_CODE;
}

// The URL parser trims C0 controls and spaces from both ends before parsing; String.trim() leaves most of them.
function trimC0ControlsAndSpaces(value: string): string {
  let start = 0;
  let end = value.length;
  while (start < end && isC0ControlOrSpace(value.codePointAt(start))) {
    start++;
  }
  while (end > start && isC0ControlOrSpace(value.codePointAt(end - 1))) {
    end--;
  }
  return value.slice(start, end);
}

// Browsers strip ASCII tab/newline before parsing, so "java\tscript:" runs as javascript:.
function isSafeUrl(value: string): boolean {
  const normalized = trimC0ControlsAndSpaces(value).replace(
    ASCII_TAB_OR_NEWLINE,
    '',
  );
  const scheme = URL_SCHEME.exec(normalized)?.[1].toLowerCase();
  if (!scheme) return true;
  return SAFE_URL_SCHEMES.has(scheme) || INLINE_IMAGE_DATA_URL.test(normalized);
}

function removeComments(root: Element): void {
  const comments = document.createTreeWalker(root, NodeFilter.SHOW_COMMENT);
  const found: Comment[] = [];
  while (comments.nextNode()) {
    found.push(comments.currentNode as Comment);
  }
  found.forEach((comment) => comment.remove());
}

function removeMsOfficeClasses(element: Element): void {
  const remaining = Array.from(element.classList).filter(
    (name) => !name.startsWith(MS_OFFICE_CLASS_PREFIX),
  );
  if (remaining.length === element.classList.length) return;

  if (remaining.length) {
    element.className = remaining.join(' ');
  } else {
    element.removeAttribute('class');
  }
}

function removeMsOfficeStyles(element: Element): void {
  const style = element.getAttribute('style');
  if (!style) return;

  let cleaned = style
    .replace(MS_OFFICE_STYLE_DECLARATION, '')
    .replace(/^;+/, '')
    .trim();

  while (cleaned.endsWith(';')) {
    cleaned = cleaned.slice(0, -1);
  }

  if (cleaned) {
    element.setAttribute('style', cleaned);
  } else {
    element.removeAttribute('style');
  }
}

function removeActiveAttributes(element: Element): void {
  for (const attribute of Array.from(element.attributes)) {
    const name = attribute.name.toLowerCase();
    const isEventHandler = name.startsWith(EVENT_HANDLER_ATTRIBUTE_PREFIX);
    const isUnsafeUrl = URL_ATTRIBUTES.has(name) && !isSafeUrl(attribute.value);
    if (isEventHandler || isUnsafeUrl) {
      element.removeAttribute(attribute.name);
    }
  }
}

export function toRichTextFragment(
  html: string | null | undefined,
): string | null {
  if (!html?.trim()) return null;

  const body = new DOMParser().parseFromString(html, 'text/html').body;
  body
    .querySelectorAll(DISCARDED_ELEMENTS)
    .forEach((element) => element.remove());
  removeComments(body);
  body.querySelectorAll('*').forEach((element) => {
    removeActiveAttributes(element);
    removeMsOfficeClasses(element);
    removeMsOfficeStyles(element);
  });

  const hasContent =
    !!body.textContent?.trim() || !!body.querySelector('img, hr, table');

  return hasContent ? body.innerHTML.trim() : null;
}
